import type { ModeId, Rules } from './types'

/**
 * Bump whenever a number below changes. Replays are verified by re-running the
 * simulation server-side, so a rules change invalidates every stored replay —
 * the leaderboard is partitioned by this value.
 */
export const RULES_VERSION = 1

const OUROBOROS: Rules = {
  id: 'ouroboros',
  label: 'Ouroboros',
  tagline: 'Burn. Coil. Devour yourself.',
  grid: 23,
  startLength: 5,
  baseTickMs: 135,
  msPerCoil: 7,
  minTickMs: 68,
  motesPerCoil: 8,

  lightEnabled: true,
  lightMax: 100,
  lightStart: 72,
  lightDrainBase: 0.22,
  lightDrainPerSegment: 0.012,
  lightDrainPerCoil: 0.08,
  moteLight: 16,
  emberLight: 5,
  prismLight: 25,
  biteLightPerSegment: 4,

  biteEnabled: true,
  biteCooldown: 90,
  biteMinLength: 8,
  emberTtl: 150,
  recoilTicks: 10,

  chainWindow: 45,
  chainMax: 12,

  prismEvery: [260, 420],
  prismTtl: 110,
  phaseTicks: 60,

  bloomsEnabled: true,
  bloomsPerCoil: 1,
  bloomCap: 14,

  maxMotes: 1,
}

/**
 * 1978 rules, 2026 presentation. No light, no bite, no blooms — you against a
 * grid. Kept honest so the classic leaderboard means something.
 */
const PURE: Rules = {
  ...OUROBOROS,
  id: 'pure',
  label: 'Pure',
  tagline: 'The old covenant. One life, four directions.',
  grid: 21,
  baseTickMs: 130,
  msPerCoil: 9,
  minTickMs: 62,
  motesPerCoil: 6,
  lightEnabled: false,
  biteEnabled: false,
  bloomsEnabled: false,
  prismEvery: [1e9, 1e9],
  chainWindow: 0,
  chainMax: 0,
}

/** Ouroboros rules, but the seed is the same for everyone on a given UTC day. */
const DAILY: Rules = {
  ...OUROBOROS,
  id: 'daily',
  label: 'Daily Coil',
  tagline: 'One seed. One world. Everyone gets the same snake.',
}

export const RULES: Record<ModeId, Rules> = {
  ouroboros: OUROBOROS,
  pure: PURE,
  daily: DAILY,
}

export const MODE_ORDER: ModeId[] = ['ouroboros', 'daily', 'pure']

export function rulesFor(mode: ModeId): Rules {
  return RULES[mode] ?? OUROBOROS
}

/** How long a tick lasts in wall-clock time. Pure presentation, never sim. */
export function tickMs(rules: Rules, coil: number, recoil: number): number {
  const base = Math.max(rules.minTickMs, rules.baseTickMs - (coil - 1) * rules.msPerCoil)
  return recoil > 0 ? base * 0.6 : base
}

export const COIL_NAMES = [
  'The Ember',
  'The Lattice',
  'The Hollow',
  'The Spiral',
  'The Eye',
  'The Long Dark',
  'The Kiln',
  'The Threshold',
  'The Devouring',
  'The Last Coil',
]

export function coilName(coil: number): string {
  return COIL_NAMES[Math.min(coil, COIL_NAMES.length) - 1] ?? 'Beyond'
}
